"use client";

import { cn } from "@/lib/utils";
import { type VariantProps, cva } from "class-variance-authority";
import { forwardRef, useMemo } from "react";
import type React from "react";

const cardVariants = cva("relative w-full overflow-hidden", {
  variants: {
    variant: {
      default:
        "rounded-lg border border-border bg-card text-card-foreground shadow-sm",
      frosted:
        "rounded-xl border border-border backdrop-blur-md bg-background/60",
      transparent: "rounded-lg",
    },
    size: {
      sm: "",
      md: "",
      lg: "",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "md",
  },
});

const cardContentPadding = cva("", {
  variants: {
    size: {
      sm: "p-3",
      md: "p-4",
      lg: "p-6",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

const cardHeaderVariants = cva("flex flex-col space-y-1.5", {
  variants: {
    size: {
      sm: "pb-2",
      md: "pb-3",
      lg: "pb-4",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

const cardContentVariants = cva("", {
  variants: {
    size: {
      sm: "space-y-2",
      md: "space-y-3",
      lg: "space-y-4",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

const cardFooterVariants = cva("flex items-center", {
  variants: {
    size: {
      sm: "pt-2",
      md: "pt-3",
      lg: "pt-4",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {
  backgroundImage?: string;
}

export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  description?: string;
  size?: "sm" | "md" | "lg";
}

export interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: "sm" | "md" | "lg";
}

export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: "sm" | "md" | "lg";
}

const CardRoot = forwardRef<HTMLDivElement, CardProps>(
  (
    { className, variant, size, backgroundImage, style, children, ...props },
    ref,
  ) => {
    const backgroundStyle = useMemo(
      () =>
        backgroundImage ? { backgroundImage: `url('${backgroundImage}')` } : {},
      [backgroundImage],
    );

    return (
      <div
        ref={ref}
        className={cn(cardVariants({ variant, size }), className)}
        style={style}
        {...props}
      >
        {variant === "frosted" && backgroundImage ? (
          <div className="grid grid-rows-1 grid-cols-1 rounded-xl overflow-hidden h-full">
            {/* Frost Surface Layer - Bottom layer */}
            <div className="row-start-1 col-start-1 rounded-xl overflow-hidden h-full">
              <div className="relative h-full">
                {/* Frost Surface with backdrop blur */}
                <div className="absolute backdrop-blur-[32px] backdrop-filter inset-0">
                  <div className="absolute bg-background/45 inset-0" />
                </div>

                {/* Frost effect gradient overlay */}
                <div className="absolute bg-gradient-to-br from-white/[0.08] via-transparent to-transparent inset-0" />
              </div>
            </div>

            {/* Background Layer - On top of frost (not blurred) */}
            <div className="row-start-1 col-start-1 h-[200px] w-full self-start overflow-hidden relative h-full">
              {/* Background image with alpha mask */}
              <div
                className="absolute top-0 left-0 h-[200px] w-full bg-cover bg-center bg-no-repeat [mask-image:linear-gradient(to_bottom,rgba(0,0,0,100%)_0%,rgba(0,0,0,0%)_100%)] [mask-size:100%_100%] [mask-repeat:no-repeat] [mask-type:alpha]"
                style={backgroundStyle}
              />

              {/* Left to right gradient overlay */}
              <div className="absolute top-0 left-0 h-[200px] w-full bg-gradient-to-r from-black/60 from-[10%] to-50% to-transparent [mask-image:linear-gradient(to_bottom,rgba(0,0,0,100%)_0%,rgba(0,0,0,0%)_100%)]" />
            </div>

            {/* Content Layer - Natural stacking order with padding */}
            <div
              className={cn(
                "row-start-1 col-start-1 relative z-10",
                cardContentPadding({ size }),
              )}
            >
              {children}
            </div>

            {/* Border Layer - Must be last to be on top */}
            <div className="row-start-1 col-start-1 pointer-events-none">
              <div className="absolute inset-0 border border-border rounded-xl" />
            </div>
          </div>
        ) : (
          <div className={cardContentPadding({ size })}>{children}</div>
        )}
      </div>
    );
  },
);

const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, title, description, size, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardHeaderVariants({ size }), className)}
      {...props}
    >
      {(title || description) && (
        <div className="space-y-1">
          {title && (
            <h3 className="font-suisse font-semibold text-lg text-foreground leading-6 cursor-default">
              {title}
            </h3>
          )}
          {description && (
            <p className="font-suisse text-sm text-muted-foreground leading-5 cursor-default">
              {description}
            </p>
          )}
        </div>
      )}
      {children}
    </div>
  ),
);

const CardContent = forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, size, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardContentVariants({ size }), className)}
      {...props}
    />
  ),
);

const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, size, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardFooterVariants({ size }), className)}
      {...props}
    />
  ),
);
CardFooter.displayName = "Card.Footer";

export { CardRoot as Card, CardHeader, CardContent, CardFooter };
